import { ValidationErrorDisplay } from '@/shared/components/ValidationErrorDisplay';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useScrollToErrorAlert } from '@/shared/hooks/useScrollToErrorAlert';
import type { UserPermission } from '@/shared/types/permissions';
import { CAlert, CButton, CCard, CCardBody, CCardHeader, CForm } from '@coreui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface EntityFormProps {
  title: string;
  isEdit: boolean;
  error?: string | null;
  listEndpoint: string;
  onSubmit: (e: React.FormEvent) => void;
  children: React.ReactNode;
  requiredPermission: UserPermission;
  readonly?: boolean;
  submitButtonDisabled?: boolean;
  submitButtonLabel?: string;
  submitButtonFirst?: boolean;
  onCancel?: () => void;
}

export const EntityForm: React.FC<EntityFormProps> = ({
  title,
  isEdit,
  error,
  listEndpoint,
  onSubmit,
  children,
  requiredPermission,
  readonly = false,
  submitButtonDisabled = false,
  submitButtonLabel,
  submitButtonFirst = false,
  onCancel,
}) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { errorAlertRef, isErrorVisible, setIsErrorVisible } = useScrollToErrorAlert(error);

  const handleFormSubmit = (e: React.FormEvent) => {
    setIsErrorVisible(true);
    onSubmit(e);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      void navigate(listEndpoint.replace('/admin', ''));
    }
  };

  if (!hasPermission(requiredPermission)) {
    return (
      <CCard>
        <CCardHeader>
          <h4 className="mb-0">{title}</h4>
        </CCardHeader>
        <CCardBody>
          <CAlert color="danger">
            You don't have permission to {isEdit ? 'edit' : 'create'} this item.
          </CAlert>
          <CButton type="button" color="secondary" variant="outline" onClick={handleCancel}>
            Back to List
          </CButton>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard>
      <CCardHeader>
        <h4 className="mb-0">{title}</h4>
      </CCardHeader>
      <CCardBody>
        {error && isErrorVisible && (
          <div ref={errorAlertRef}>
            <CAlert color="danger" dismissible onClose={() => setIsErrorVisible(false)}>
              <ValidationErrorDisplay message={error} />
            </CAlert>
          </div>
        )}

        <CForm onSubmit={handleFormSubmit}>
          {children}

          <div className="d-flex gap-2 mt-4 justify-content-end">
            <CButton
              type="button"
              color="secondary"
              variant="outline"
              className={submitButtonFirst ? 'order-1' : undefined}
              onClick={handleCancel}
            >
              {readonly ? 'Back' : 'Cancel'}
            </CButton>
            {!readonly && (
              <CButton type="submit" color="primary" disabled={submitButtonDisabled}>
                {submitButtonLabel ?? (isEdit ? 'Update' : 'Save')}
              </CButton>
            )}
          </div>
        </CForm>
      </CCardBody>
    </CCard>
  );
};
